/**
 * JobsPreferencesHydrationBanner.js — RC 1.43.B
 *
 * Slim transparency strip rendered inside the JobsPage preferences
 * bar. Tells the candidate exactly how many of their saved
 * preferences are actively informing scoring + filtering, with an
 * empty-state nudge to set them when none are configured.
 *
 * Reads the same `/api/profile/preferences` endpoint as
 * PreferencesCard. Refreshes itself when the editor saves
 * (`ndor:preferences-changed` window event).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Info, Settings2, Sparkles,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import {
  countActivePreferences, describeActivePreferences,
} from '@/components/preferencesHelpers';

export default function JobsPreferencesHydrationBanner() {
  const [prefs, setPrefs]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [unmigrated, setUnmig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/profile/preferences');
      setPrefs(r?.preferences || {});
      setUnmig(Boolean(r?.unmigrated));
    } catch {
      setPrefs(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when the editor saves.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener('ndor:preferences-changed', onChanged);
    return () => window.removeEventListener('ndor:preferences-changed', onChanged);
  }, [load]);

  const count   = useMemo(() => countActivePreferences(prefs), [prefs]);
  const summary = useMemo(() => describeActivePreferences(prefs), [prefs]);

  if (loading) return null;

  // Empty state — nudge the user to set preferences.
  if (count === 0) {
    return (
      <div
        className="flex items-start gap-2 px-3 py-2.5 rounded border border-ndor-edge bg-ndor-surface-alt/40 text-xs text-ndor-ink"
        data-testid="jobs-preferences-hydration-banner-empty"
      >
        <Info className="h-4 w-4 text-ndor-ink-muted shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">No preferences set yet.</p>
          <p className="text-ndor-ink-muted mt-0.5">
            NDOR Analyse Fit will fall back to job-side signals only.{' '}
            <Link
              to="/account?tab=preferences"
              className="underline underline-offset-2 hover:text-ndor-ink-muted"
              data-testid="jobs-preferences-hydration-banner-set-link"
            >
              Set your preferences
            </Link>{' '}
            to make scoring personal — salary, industry, seniority, exclusions, and more.
          </p>
        </div>
      </div>
    );
  }

  // Active state — confirm what's hydrating the score.
  return (
    <div
      className="flex items-start gap-2 px-3 py-2.5 rounded border border-ndor-edge bg-ndor-surface-alt/40 text-xs text-ndor-ink"
      data-testid="jobs-preferences-hydration-banner"
    >
      <Sparkles className="h-4 w-4 text-ndor-ink-muted shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-medium flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          NDOR Analyse Fit is using {count} of your preference{count === 1 ? '' : 's'}.
        </p>
        {summary.length > 0 && (
          <ul
            className="text-ndor-ink-muted mt-1 space-y-0.5 list-disc pl-4 marker:text-ndor-ink-muted/60"
            data-testid="jobs-preferences-hydration-summary"
          >
            {summary.slice(0, 4).map((line) => (
              <li key={line} className="truncate">{line}</li>
            ))}
            {summary.length > 4 && (
              <li className="italic">+ {summary.length - 4} more…</li>
            )}
          </ul>
        )}
        {unmigrated && (
          <p className="text-amber-200 mt-1">
            Storage column missing — apply migration 023 to persist.
          </p>
        )}
      </div>
      <Link
        to="/account?tab=preferences"
        className="text-ndor-ink-muted hover:text-ndor-ink shrink-0 inline-flex items-center gap-1 text-[11px] underline underline-offset-2"
        data-testid="jobs-preferences-hydration-banner-edit-link"
      >
        <Settings2 className="h-3 w-3" />
        Edit
      </Link>
    </div>
  );
}
